'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import {
  Eye, EyeOff, ArrowRight, CheckCircle2, Shield, Zap, Users, Check,
  Building2, Phone, Mail, Lock, User, FileText, AlertCircle, Loader2,
  RefreshCw, ArrowLeft, ShieldCheck,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/context/AuthContext';
import { createClient } from '@/lib/supabase/client';

type LoginFormData = {
  email: string;
  password: string;
  rememberMe: boolean;
};

type SignupFormData = {
  centreName: string;
  subdomain: string;
  adminName: string;
  phone: string;
  gst: string;
  email: string;
  password: string;
  confirmPassword: string;
  dpdpConsent: boolean;
  termsAccepted: boolean;
};

type SignupStep = 'form' | 'submitting' | 'complete';
type LoginStep = 'credentials' | '2fa';

const features = [
  { icon: Zap, text: 'SOAP-compliant session notes & goal tracking' },
  { icon: Users, text: 'Multi-role access: Admin, Therapist, Patient' },
  { icon: Shield, text: 'DPDP-compliant data handling for India' },
  { icon: FileText, text: 'GST-ready billing & financial reports' },
];

// ─── OTP Input Component ──────────────────────────────────────────────────────

function OtpInput({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.padEnd(6, '').split('').slice(0, 6);

  const handleChange = (idx: number, val: string) => {
    if (!/^\d*$/.test(val)) return;
    const newDigits = [...digits];
    newDigits[idx] = val.slice(-1);
    onChange(newDigits.join(''));
    if (val && idx < 5) inputs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) { onChange(pasted.padEnd(6, '')); inputs.current[Math.min(pasted.length, 5)]?.focus(); }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: 6 }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => { inputs.current[idx] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[idx] || ''}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-11 h-12 text-center text-lg font-bold border border-input rounded-xl bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 transition-all"
        />
      ))}
    </div>
  );
}

// ─── 2FA Verification Step ────────────────────────────────────────────────────

function TwoFAStep({
  email,
  method,
  onVerified,
  onBack,
}: {
  email: string;
  method: 'email' | 'sms' | 'totp';
  onVerified: () => void;
  onBack: () => void;
}) {
  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(60);

  useEffect(() => {
    const interval = setInterval(() => setResendCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(interval);
  }, []);

  const isTOTP = method === 'totp';

  const handleVerify = async () => {
    if (otp.length < 6) { setError('Please enter the complete 6-digit code.'); return; }
    setIsVerifying(true);
    setError('');
    await new Promise((res) => setTimeout(res, 1000));
    setIsVerifying(false);
    toast.success('Identity verified successfully!');
    onVerified();
  };

  const handleResend = async () => {
    if (isTOTP) return;
    setIsResending(true);
    await new Promise((res) => setTimeout(res, 800));
    setIsResending(false);
    setResendCooldown(60);
    toast.success(`Code resent via ${method === 'sms' ? 'SMS' : 'email'}.`);
  };

  const methodLabel = isTOTP ? 'Authenticator App' : method === 'sms' ? 'SMS' : 'Email';
  const methodDesc = isTOTP
    ? 'Open your authenticator app and enter the 6-digit code'
    : method === 'sms'
    ? `We sent a 6-digit code via SMS to the number on your account`
    : `We sent a 6-digit code to ${email}`;

  return (
    <div className="animate-slide-up">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft size={15} /> Back to Sign In
      </button>

      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4">
          <ShieldCheck size={28} className="text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground">Two-Factor Verification</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">{methodDesc}</p>
        <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
          <Shield size={12} className="text-primary" />
          <span className="text-xs font-semibold text-primary">{methodLabel}</span>
        </div>
      </div>

      <div className="space-y-5">
        <OtpInput value={otp} onChange={setOtp} disabled={isVerifying} />

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <AlertCircle size={14} className="text-danger shrink-0" />
            <p className="text-xs text-danger">{error}</p>
          </div>
        )}

        <button
          onClick={handleVerify}
          disabled={isVerifying || otp.length < 6}
          className="btn-primary w-full py-3 disabled:opacity-50"
        >
          {isVerifying ? <Loader2 size={16} className="animate-spin" /> : <>Verify & Sign In <ArrowRight size={16} /></>}
        </button>

        {!isTOTP && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Didn't receive the code?</p>
            {resendCooldown > 0 ? (
              <p className="text-sm text-muted-foreground mt-1">Resend in <span className="font-semibold text-foreground">{resendCooldown}s</span></p>
            ) : (
              <button
                onClick={handleResend}
                disabled={isResending}
                className="text-sm text-primary hover:underline mt-1 flex items-center gap-1 mx-auto"
              >
                {isResending ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                Resend Code
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Registration Complete Step ───────────────────────────────────────────────

function RegistrationCompleteStep({ centreName, email, onGoToLogin }: { centreName: string; email: string; onGoToLogin: () => void }) {
  return (
    <div className="animate-slide-up text-center">
      <div className="w-20 h-20 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 size={36} className="text-emerald-600" />
      </div>
      <h2 className="text-xl font-bold text-foreground mb-2">Registration Complete!</h2>
      <p className="text-sm text-muted-foreground mb-2">
        <span className="font-semibold text-foreground">{centreName}</span> has been successfully registered.
      </p>
      <p className="text-sm text-muted-foreground mb-6">
        A confirmation email has been sent to <span className="font-semibold text-foreground">{email}</span>. Please verify your email before signing in.
      </p>

      <div className="space-y-3 mb-8">
        {[
          { icon: CheckCircle2, text: 'Centre account created', color: 'text-emerald-600' },
          { icon: CheckCircle2, text: 'Admin profile set up', color: 'text-emerald-600' },
          { icon: Mail, text: 'Verification email sent — check your inbox', color: 'text-blue-600' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border">
            <item.icon size={16} className={`shrink-0 ${item.color}`} />
            <p className="text-sm text-foreground">{item.text}</p>
          </div>
        ))}
      </div>

      <button onClick={onGoToLogin} className="btn-primary w-full py-3">
        Go to Sign In <ArrowRight size={16} />
      </button>
    </div>
  );
}

// ─── Google OAuth Button ──────────────────────────────────────────────────────

function GoogleSignInButton() {
  const [loading, setLoading] = React.useState(false);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) {
        toast.error('Google sign-in failed. Please try again.');
        setLoading(false);
      }
    } catch {
      toast.error('Google sign-in failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleGoogleSignIn}
      disabled={loading}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-input bg-card hover:bg-muted transition-all text-sm font-semibold text-foreground disabled:opacity-50"
    >
      {loading ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      )}
      Continue with Google
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LoginPageClient() {
  const router = useRouter();
  const { login, user } = useAuth();
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [loginStep, setLoginStep] = useState<LoginStep>('credentials');
  const [signupStep, setSignupStep] = useState<SignupStep>('form');
  const [signupData, setSignupData] = useState<SignupFormData | null>(null);
  const [pendingLoginEmail, setPendingLoginEmail] = useState('');
  const [pending2FAMethod, setPending2FAMethod] = useState<'email' | 'sms' | 'totp'>('email');
  const [pendingLoginData, setPendingLoginData] = useState<LoginFormData | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loginForm = useForm<LoginFormData>({ defaultValues: { email: '', password: '', rememberMe: false } });
  const signupForm = useForm<SignupFormData>({
    defaultValues: {
      centreName: '', subdomain: '', adminName: '', phone: '', gst: '',
      email: '', password: '', confirmPassword: '',
      dpdpConsent: false, termsAccepted: false,
    }
  });

  // Check if 2FA is required for this user's tenant
  const check2FARequired = async (email: string): Promise<{ required: boolean; method: 'email' | 'sms' | 'totp' }> => {
    try {
      const supabase = createClient();
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('tenant_id, role')
        .eq('email', email)
        .maybeSingle();

      if (!profile?.tenant_id) return { required: false, method: 'email' };

      const { data: twofa } = await supabase
        .from('tenant_2fa_settings')
        .select('*')
        .eq('tenant_id', profile.tenant_id)
        .maybeSingle();

      if (!twofa) return { required: false, method: 'email' };

      const role = profile.role as string;
      let required = false;
      if (role === 'admin' && twofa.admin_2fa_enabled) required = true;
      if (role === 'therapist' && twofa.therapist_2fa_enabled) required = true;
      if (role === 'patient' && twofa.patient_2fa_enabled) required = true;

      return { required, method: (twofa.method as 'email' | 'sms' | 'totp') ?? 'email' };
    } catch {
      return { required: false, method: 'email' };
    }
  };

  const redirectByRole = (role: string) => {
    if (role === 'patient') {
      router.push('/patient-dashboard');
    } else if (role === 'therapist') {
      router.push('/therapist-dashboard');
    } else if (role === 'super_admin') {
      router.push('/super-admin/dashboard');
    } else {
      router.push('/dashboard');
    }
  };

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const { required, method } = await check2FARequired(data.email);
      if (required) {
        setIsLoading(false);
        setPendingLoginEmail(data.email);
        setPending2FAMethod(method);
        setPendingLoginData(data);
        setLoginStep('2fa');
        toast.info(`2FA code sent via ${method === 'totp' ? 'authenticator app' : method}`);
        return;
      }

      const result = await login(data.email, data.password);
      setIsLoading(false);

      if (!result.success) {
        loginForm.setError('password', { message: result.error ?? 'Invalid email or password. Please try again.' });
        return;
      }

      // Fetch role from user_profiles for redirect
      const supabase = createClient();
      const { data: { user: sbUser } } = await supabase.auth.getUser();
      if (sbUser) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', sbUser.id)
          .maybeSingle();
        const role = (profile as Record<string, unknown> | null)?.role as string ?? 'admin';
        redirectByRole(role);
      } else {
        router.push('/dashboard');
      }
    } catch {
      setIsLoading(false);
      loginForm.setError('password', { message: 'Something went wrong. Please try again.' });
    }
  };

  const handle2FAVerified = async () => {
    if (!pendingLoginData) return;
    setIsLoading(true);
    const result = await login(pendingLoginData.email, pendingLoginData.password);
    setIsLoading(false);
    if (!result.success) {
      toast.error(result.error ?? 'Login failed. Please try again.');
      return;
    }
    const supabase = createClient();
    const { data: { user: sbUser } } = await supabase.auth.getUser();
    if (sbUser) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', sbUser.id)
        .maybeSingle();
      const role = (profile as Record<string, unknown> | null)?.role as string ?? 'admin';
      redirectByRole(role);
    } else {
      router.push('/dashboard');
    }
  };

  const onSignupSubmit = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    if (!data.dpdpConsent || !data.termsAccepted) {
      toast.error('Please accept DPDP consent and Terms of Service to continue.');
      return;
    }

    setIsLoading(true);
    setSignupStep('submitting');

    try {
      // Call the server-side registration API (uses service role to bypass RLS)
      const res = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centreName: data.centreName,
          subdomain: data.subdomain,
          adminName: data.adminName,
          phone: data.phone,
          gst: data.gst,
          email: data.email,
          password: data.password,
        }),
      });

      const result = await res.json();

      if (!res.ok) {
        setIsLoading(false);
        setSignupStep('form');

        if (result.code === 'USER_EXISTS_COMPLETE') {
          // Account fully exists — redirect to login
          toast.info('Account already registered. Please sign in.');
          setActiveTab('login');
          loginForm.setValue('email', data.email);
          return;
        }

        // Show error on the relevant field
        if (result.error?.toLowerCase().includes('email')) {
          signupForm.setError('email', { message: result.error });
        } else if (result.error?.toLowerCase().includes('workspace') || result.error?.toLowerCase().includes('subdomain')) {
          signupForm.setError('subdomain', { message: result.error });
        } else {
          toast.error(result.error ?? 'Registration failed. Please try again.');
        }
        return;
      }

      // Success
      setIsLoading(false);
      setSignupData(data);
      setSignupStep('complete');
    } catch (err: unknown) {
      setIsLoading(false);
      setSignupStep('form');
      toast.error(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    }
  };

  const handleSubdomainGenerate = () => {
    const centre = signupForm.watch('centreName');
    if (centre) {
      const slug = centre.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20);
      signupForm.setValue('subdomain', slug);
    }
  };

  const handleGoToLogin = () => {
    setActiveTab('login');
    setSignupStep('form');
    setSignupData(null);
    signupForm.reset();
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] 2xl:w-[560px] gradient-primary flex-col justify-between p-10 shrink-0 relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-white/5" />
        <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-white/5" />
        <div className="absolute top-1/2 -right-8 w-32 h-32 rounded-full bg-white/5" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={56} />
          </div>

          <h1 className="text-hero-xl font-extrabold text-white mb-4 leading-tight">
            India's most complete therapy centre platform
          </h1>
          <p className="text-blue-100 text-base leading-relaxed mb-10">
            From SOAP notes to GST invoices — manage every aspect of your practice in one DPDP-compliant workspace.
          </p>

          <div className="space-y-4">
            {features.map((f) => (
              <div key={`feat-${f.text.slice(0, 20)}`} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0 mt-0.5">
                  <f.icon size={16} className="text-white" />
                </div>
                <p className="text-blue-50 text-sm leading-snug">{f.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-white/10 border border-white/20">
            <CheckCircle2 size={20} className="text-emerald-300 shrink-0" />
            <div>
              <p className="text-white text-sm font-semibold">DPDP Act 2023 Compliant</p>
              <p className="text-blue-200 text-xs">All patient data handled as per India's Digital Personal Data Protection Act</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={44} />
          </div>

          {/* Tab switcher */}
          {(activeTab === 'login' && loginStep === 'credentials') || (activeTab === 'signup' && signupStep === 'form') ? (
            <div className="flex bg-muted rounded-xl p-1 mb-8">
              {(['login', 'signup'] as const).map((tab) => (
                <button
                  key={`tab-${tab}`}
                  onClick={() => { setActiveTab(tab); setSignupStep('form'); setLoginStep('credentials'); }}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${activeTab === tab ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {tab === 'login' ? 'Sign In' : 'Register Centre'}
                </button>
              ))}
            </div>
          ) : null}

          {/* ── LOGIN FLOW ── */}
          {activeTab === 'login' && (
            <>
              {loginStep === 'credentials' && (
                <div className="animate-slide-up">
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-foreground">Welcome back</h2>
                    <p className="text-sm text-muted-foreground mt-1">Sign in to your TherapiQ workspace</p>
                  </div>

                  <div className="mb-5">
                    <GoogleSignInButton />
                    <div className="flex items-center gap-3 mt-4">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs font-medium text-muted-foreground px-2">or sign in with email</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                  </div>

                  <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="login-email">Email address</label>
                      <div className="relative">
                        <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          id="login-email"
                          type="email"
                          autoComplete="email"
                          placeholder="you@yourcentre.in"
                          className="input-field pl-9"
                          suppressHydrationWarning
                          {...loginForm.register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Enter a valid email' } })}
                        />
                      </div>
                      {loginForm.formState.errors.email && (
                        <p className="mt-1.5 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{loginForm.formState.errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-sm font-medium text-foreground" htmlFor="login-password">Password</label>
                        <button type="button" className="text-xs text-primary hover:underline">Forgot password?</button>
                      </div>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          placeholder="••••••••"
                          className="input-field pl-9 pr-10"
                          suppressHydrationWarning
                          {...loginForm.register('password', { required: 'Password is required' })}
                        />
                        <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                          {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                      {loginForm.formState.errors.password && (
                        <p className="mt-1.5 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{loginForm.formState.errors.password.message}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <input id="remember-me" type="checkbox" className="w-4 h-4 rounded border-input text-primary focus:ring-ring" {...loginForm.register('rememberMe')} />
                      <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer">Keep me signed in for 30 days</label>
                    </div>

                    <button type="submit" disabled={isLoading} className="btn-primary w-full py-3">
                      {isLoading ? <Loader2 size={16} className="animate-spin" /> : <>Sign In <ArrowRight size={16} /></>}
                    </button>
                  </form>
                </div>
              )}

              {loginStep === '2fa' && (
                <TwoFAStep
                  email={pendingLoginEmail}
                  method={pending2FAMethod}
                  onVerified={handle2FAVerified}
                  onBack={() => setLoginStep('credentials')}
                />
              )}
            </>
          )}

          {/* ── SIGNUP FLOW ── */}
          {activeTab === 'signup' && (
            <>
              {(signupStep === 'form' || signupStep === 'submitting') && (
                <div className="animate-slide-up">
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-foreground">Register your centre</h2>
                    <p className="text-sm text-muted-foreground mt-1">Set up your TherapiQ workspace in under 2 minutes</p>
                  </div>

                  <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="centre-name">Therapy Centre Name <span className="text-danger">*</span></label>
                      <div className="relative">
                        <Building2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input id="centre-name" type="text" placeholder="Sunrise Therapy Centre" className="input-field pl-9"
                          {...signupForm.register('centreName', { required: 'Centre name is required' })} onBlur={handleSubdomainGenerate} />
                      </div>
                      {signupForm.formState.errors.centreName && (
                        <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{signupForm.formState.errors.centreName.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="subdomain">Workspace URL <span className="text-danger">*</span></label>
                      <p className="text-xs text-muted-foreground mb-1.5">Your patients will book via this URL</p>
                      <div className="flex rounded-lg border border-input overflow-hidden focus-within:ring-2 focus-within:ring-ring">
                        <input id="subdomain" type="text" placeholder="sunrisetherapy" className="flex-1 px-3 py-2.5 text-sm text-foreground bg-card focus:outline-none"
                          {...signupForm.register('subdomain', { required: 'Subdomain is required', pattern: { value: /^[a-z0-9]+$/, message: 'Only lowercase letters and numbers' }, minLength: { value: 3, message: 'Minimum 3 characters' } })} />
                        <span className="px-3 py-2.5 bg-muted text-muted-foreground text-sm border-l border-input">.therapiq.in</span>
                      </div>
                      {signupForm.formState.errors.subdomain && (
                        <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{signupForm.formState.errors.subdomain.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="admin-name">Your Full Name <span className="text-danger">*</span></label>
                      <div className="relative">
                        <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input id="admin-name" type="text" placeholder="Priya Ramachandran" className="input-field pl-9"
                          {...signupForm.register('adminName', { required: 'Your name is required' })} />
                      </div>
                      {signupForm.formState.errors.adminName && (
                        <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{signupForm.formState.errors.adminName.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="phone">Mobile Number <span className="text-danger">*</span></label>
                      <div className="flex rounded-lg border border-input overflow-hidden focus-within:ring-2 focus-within:ring-ring">
                        <span className="px-3 py-2.5 bg-muted text-muted-foreground text-sm border-r border-input flex items-center gap-1.5">
                          <Phone size={13} /> +91
                        </span>
                        <input id="phone" type="tel" placeholder="98765 43210" className="flex-1 px-3 py-2.5 text-sm text-foreground bg-card focus:outline-none"
                          {...signupForm.register('phone', { required: 'Phone number is required', pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number' } })} />
                      </div>
                      {signupForm.formState.errors.phone && (
                        <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{signupForm.formState.errors.phone.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="gst">GSTIN <span className="text-muted-foreground font-normal">(optional)</span></label>
                      <p className="text-xs text-muted-foreground mb-1.5">Required for GST-compliant invoicing</p>
                      <input id="gst" type="text" placeholder="22AAAAA0000A1Z5" className="input-field uppercase" maxLength={15}
                        {...signupForm.register('gst', { pattern: { value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, message: 'Enter a valid 15-digit GSTIN' } })} />
                      {signupForm.formState.errors.gst && (
                        <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{signupForm.formState.errors.gst.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="signup-email">Work Email <span className="text-danger">*</span></label>
                      <div className="relative">
                        <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input id="signup-email" type="email" placeholder="admin@yourcentre.in" className="input-field pl-9"
                          {...signupForm.register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Enter a valid email' } })} />
                      </div>
                      {signupForm.formState.errors.email && (
                        <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{signupForm.formState.errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="signup-password">Password <span className="text-danger">*</span></label>
                      <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters with at least one number and symbol</p>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input id="signup-password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" className="input-field pl-9 pr-10"
                          {...signupForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' }, pattern: { value: /^(?=.*[0-9])(?=.*[!@#$%^&*])/, message: 'Must include a number and a symbol' } })} />
                        <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                      {signupForm.formState.errors.password && (
                        <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{signupForm.formState.errors.password.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="confirm-password">Confirm Password <span className="text-danger">*</span></label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input id="confirm-password" type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••" className="input-field pl-9 pr-10"
                          {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })} />
                        <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                      {signupForm.formState.errors.confirmPassword && (
                        <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{signupForm.formState.errors.confirmPassword.message}</p>
                      )}
                    </div>

                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-blue-50 border border-blue-200">
                      <input id="dpdp-consent" type="checkbox" className="w-4 h-4 mt-0.5 rounded border-blue-300 text-primary focus:ring-primary" {...signupForm.register('dpdpConsent', { required: true })} />
                      <label htmlFor="dpdp-consent" className="text-sm text-blue-800 cursor-pointer">
                        <span className="font-semibold">DPDP Act 2023 Consent <span className="text-danger">*</span></span><br />
                        <span className="text-xs text-blue-600">I confirm this centre will collect and process patient data in accordance with India's Digital Personal Data Protection Act, 2023.</span>
                      </label>
                    </div>

                    <div className="flex items-start gap-2.5">
                      <input id="terms" type="checkbox" className="w-4 h-4 mt-0.5 rounded border-input text-primary focus:ring-ring" {...signupForm.register('termsAccepted', { required: true })} />
                      <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer">
                        I agree to TherapiQ's <span className="text-primary hover:underline cursor-pointer">Terms of Service</span> and <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span> <span className="text-danger">*</span>
                      </label>
                    </div>

                    <button type="submit" disabled={isLoading} className="btn-primary w-full py-3">
                      {isLoading ? (
                        <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Creating your account…</span>
                      ) : (
                        <>Register Centre <ArrowRight size={16} /></>
                      )}
                    </button>

                    <p className="text-xs text-muted-foreground text-center">
                      A verification email will be sent to confirm your address
                    </p>
                  </form>
                </div>
              )}

              {signupStep === 'complete' && signupData && (
                <RegistrationCompleteStep
                  centreName={signupData.centreName}
                  email={signupData.email}
                  onGoToLogin={handleGoToLogin}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}